/* eslint-disable @atlassian/relay/unused-fields */
import React, {
	type ComponentPropsWithoutRef,
	forwardRef,
	useCallback,
	useMemo,
	useState,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
	ContainerAvatar as Avatar,
	useIsExpanded,
} from '@atlaskit/navigation-system';
import { OnboardingSpotlightMaybe } from '@atlassian/jira-atlassian-onboarding-nav4/src/ui/onboarding-spotlight-maybe/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { isNormalClick } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/is-normal-click.tsx';
import { useProjectsAnalyticsL1 } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l1/index.tsx';
import { ENTITY_ID } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { Nav4ExpandableMenuItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { SkeletonNavMenuItems } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import { useGetLinkProps } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-link-props/index.tsx';
import { getTestId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useExpandableEntryPointTrigger } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/use-expandable-entry-point-trigger/index.tsx';
import { removeBaseUrl } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/utils/build-project-url/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { RouteEntryPointContainer } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/route-entry-point-adapter/index.tsx';
import { LEFT_SIDEBAR } from '@atlassian/jira-entry-point-page-layout-fields/src/index.tsx';
import { usePageLayoutEntryPoint } from '@atlassian/jira-entry-point-page-layout/src/index.tsx';
import type { LeftSidebarEntryPointConfig } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { usePathParam } from '@atlassian/react-resource-router';
import type { JswExpandableProject$key } from '@atlassian/jira-relay/src/__generated__/JswExpandableProject.graphql';
import { ProjectActions } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-projects-common/src/ui/project-actions/ProjectActions.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { useProjectMenuItemDragAndDrop } from '@atlassian/jira-navigation-apps-sidebar-nav4-projects-customise-order/src/controllers/useProjectMenuItemDragAndDrop.tsx';
import { getProjectKeyFromProjectOrIssueKey } from '@atlassian/jira-router-resources-utils/src/utils/get-project-key-id/index.tsx';
import {
	useSetWillRedirectOnLoad,
	useWithSetWillRedirectOnLoad,
} from '@atlassian/redirect-on-load';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import { nav4JSWProjectEntryPoint } from './async-content/entrypoint.tsx';

type JswExpandableProjectProps = {
	projectData: JswExpandableProject$key;
	spotlight?: string;
	menuItemButtonRef?: React.RefObject<HTMLButtonElement>;
};

export const JswExpandableProject = ({
	projectData,
	spotlight,
	menuItemButtonRef,
}: JswExpandableProjectProps): React.JSX.Element | null => {
	const { sendProjectMenuItemAnalyticsEvent } = useProjectsAnalyticsL1();
	const project = useFragment<JswExpandableProject$key>(
		graphql`
			fragment JswExpandableProject on JiraProject {
				id
				projectKey: key @required(action: THROW)
				projectId @required(action: THROW)
				isFavourite
				projectType
				avatar @required(action: THROW) {
					large @required(action: THROW)
				}
				name @required(action: THROW)
				projectKey: key @required(action: THROW)
				webUrl @required(action: THROW)
				...ProjectActions
			}
		`,
		projectData,
	);
	const cloudId = useCloudId();

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const entryPointParams = useMemo(
		() => ({
			tenantContext: { cloudId },
			context: {
				match: {
					params: {
						projectKey: project.projectKey,
					},
				},
			},
		}),
		[cloudId, project.projectKey],
	) as unknown as EntryPointRouteParams;

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const leftSidebarEPRef = usePageLayoutEntryPoint(LEFT_SIDEBAR) as LeftSidebarEntryPointConfig;
	const boardsEPRef = leftSidebarEPRef?.entryPoints?.boards;

	const [projectKeyPathParam] = usePathParam('projectKey');
	const [issueKeyPathParam] = usePathParam('issueKey');

	let projectKey = projectKeyPathParam;
	if (issueKeyPathParam && fg('move-issue-view-to-entrypoint')) {
		projectKey = getProjectKeyFromProjectOrIssueKey('', issueKeyPathParam);
	}

	const shouldRenderBoardsEntryPoint = project.projectKey === projectKey;

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		nav4JSWProjectEntryPoint,
		entryPointParams,
	);

	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);

	const { getLinkProps } = useGetLinkProps();

	// Create this component once, then keep using it FOREVER.
	// We don't want to keep switching the implementation as the user does SPA transitions.
	const [entryPointConfig] = useState(() => {
		const epContainerProps = {
			id: 'jsw-expandable-project',
			packageName: PACKAGE_NAME,
			teamName: TEAM_NAME,
			errorFallback: 'flag' as const,
			fallback: <SkeletonNavMenuItems />,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			runtimeProps: entryPointParams as never,
		};

		return {
			Component: () => (
				<RouteEntryPointContainer
					epContainerProps={epContainerProps}
					jiraEPRef={entryPointReferenceSubject}
					nestedEPRef={boardsEPRef}
					placeholderEntryPoint={nav4JSWProjectEntryPoint}
					shouldRenderNestedEntryPoint={shouldRenderBoardsEntryPoint}
				/>
			),
		};
	});

	const content = () => <entryPointConfig.Component />;
	const setWillRedirectOnLoad = useSetWillRedirectOnLoad();

	const onSystemToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
			} else {
				setWillRedirectOnLoad(false);
			}
		},
		[entryPointActions, setWillRedirectOnLoad],
	);

	const onExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();

				sendProjectMenuItemAnalyticsEvent({
					starred: project.isFavourite ?? false,
					recent: !project.isFavourite,
					projectId: project.projectId,
					projectType: project.projectType,
				});
			} else {
				setWillRedirectOnLoad(false);
			}
		},
		[
			entryPointActions,
			project.isFavourite,
			project.projectId,
			project.projectType,
			sendProjectMenuItemAnalyticsEvent,
			setWillRedirectOnLoad,
		],
	);

	const onClick = useCallback((ev: React.MouseEvent) => {
		if (isNormalClick(ev)) {
			ev.preventDefault();
		}
	}, []);

	const { state, draggableAnchorRef, dropTargetRef, dropIndicator, dragPreview, hasDragIndicator } =
		useProjectMenuItemDragAndDrop({
			projectId: project.projectId,
			projectKey: project.projectKey,
			isFavourite: project.isFavourite,
			name: project.name,
			avatar: project.avatar,
			isEnabled:
				!!project.isFavourite &&
				expVal('blu-6314-drag-and-drop-projects-sidebar-experiment', 'isEnabled', false),
		});

	if (!project) {
		return null;
	}

	const menuId = ENTITY_ID.PROJECT(project.projectKey);

	return (
		<>
			<Nav4ExpandableMenuItem
				menuId={menuId}
				onSystemToggle={onSystemToggle}
				onExpansionToggle={onExpansionToggle}
				dropIndicator={dropIndicator}
				dropTargetRef={dropTargetRef}
				isDragging={state.type === 'dragging'}
			>
				<OnboardingSpotlightMaybe key={project.projectKey} spotlight={spotlight}>
					<ExpandableMenuItemTriggerWithSetWillRedirectOnLoad
						{...getLinkProps(removeBaseUrl(project.webUrl))}
						actionsOnHover={<ProjectActions projectFragment={project} />}
						elemBefore={<Avatar src={project.avatar.large} />}
						ref={mergeRefs(draggableAnchorRef, ...[triggerRef, menuItemButtonRef].filter(Boolean))}
						onClick={onClick}
						testId={getTestId(menuId)}
						hasDragIndicator={hasDragIndicator}
						isDragging={state.type === 'dragging'}
						interactionName={
							fg('blu-7963_add_ufo_interaction_names_nav') ? 'nav4-sidebar-jsw-project' : undefined
						}
					>
						{project.name}
					</ExpandableMenuItemTriggerWithSetWillRedirectOnLoad>
				</OnboardingSpotlightMaybe>
				<ExpandableMenuItemContent>{content()}</ExpandableMenuItemContent>
			</Nav4ExpandableMenuItem>
			{dragPreview}
		</>
	);
};

type ExpandableMenuItemTriggerProps = ComponentPropsWithoutRef<typeof ExpandableMenuItemTrigger>;

// A thin wrapper so we can access `isExpanded` via hook.
// We redirect on load when the label is clicked, so we only do this in `onClick` and not in any of the toggle handlers (which the chevron also uses).
const ExpandableMenuItemTriggerWithSetWillRedirectOnLoad = forwardRef<
	HTMLButtonElement | HTMLAnchorElement,
	ExpandableMenuItemTriggerProps
>(({ children, onClick, ...props }, ref) => {
	const isExpanded = useIsExpanded();

	// If not currently expanded we want to redirect on load.
	// If currently expanded, the toggle handlers above will set to false.
	const onClickActual = useWithSetWillRedirectOnLoad(onClick, true, !isExpanded);

	return (
		<ExpandableMenuItemTrigger onClick={onClickActual} ref={ref} {...props}>
			{children}
		</ExpandableMenuItemTrigger>
	);
});
